import { useEffect, useState } from 'react';

import { Box, Button, InputAdornment, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { ArrowBack, Person, Search, Verified } from '@mui/icons-material'

import { supabase } from '../supabaseClient.js';
import { JamPadEvent, JamPadProfileSearchResult, UserType } from '../DataModels.ts';
import EventPreview from '../Events/EventPreview.tsx';
import EventItem from '../Events/EventItem.tsx';
import { UUID } from 'crypto';
import { useNavigate } from 'react-router-dom';
import { EventMapContext } from '../App/App.tsx';

export default function SearchPage() {
    const nav = useNavigate();
    const profileResultLimit = 10;
    const searchDelay = 400; // debounce in ms

    const [search, setSearch] = useState("");
    const [searchBuffer, setBuff] = useState("");

    const [profileResults, setProfiles] = useState<JamPadProfileSearchResult[]>([]);
    const [eventResults, setEvents] = useState<Map<UUID, JamPadEvent>>(new Map());
    const [fullEventID, setID] = useState<UUID | undefined>(undefined);

    useEffect(() => {
        const handler = setTimeout(() => {
            setBuff(search);

        }, searchDelay);
        return () => { clearTimeout(handler); }; // prevents multiple searches during the delay
    }, [search,])
    useEffect(() => {
        if (searchBuffer !== "") {
            searchForProfiles();
            searchForEvents();
        }
    }, [searchBuffer]);

    async function searchForEvents() {
        const { data, error } = await supabase
            .rpc("search_for_events", { term: search, tags_in: [] })
            .select().overrideTypes<JamPadEvent[]>();
        if(error) {
            console.error("Event search failed: " + error.message);
        } else {
            const m = new Map();
            data.forEach((e) => {
                e.date = new Date(e.date);
                m.set(e.id, e);
            });
            setEvents(m);
        }
    }
    async function searchForProfiles() {
        const { data, error } = await supabase.from("profiles")
            .select("id, display_name, username, pfp, type")
            .or(`username.ilike.%${search}%, display_name.ilike.%${search})%`)
            .limit(profileResultLimit).overrideTypes<JamPadProfileSearchResult[]>();
        if(error) {
            console.error("Profile search failed: " + error.message);
        } else {
            setProfiles(data);
        }
    }

    return (<EventMapContext.Provider value={{ events: eventResults, setEvents: (m) => {setEvents(m)} }}>
        <Box sx={{ flex: '1', background: '#272636', 
            padding: { xs: '0.5vh 2vw', md: '2vh 4vw' }, 
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            position: 'relative'
        }}>
            <TextField style={{ margin: '14px 0', background: 'white', borderRadius: '4px' }}
                onChange={(e) => setSearch(e.target.value)}
                variant="outlined" fullWidth placeholder='Search for artists or events...'
                slotProps={{ input: {
                    startAdornment: (<InputAdornment position="start">
                        <Search/>
                    </InputAdornment>)
                }
            }}
            />
            <Box sx={{ flex: '1', overflow: 'hidden auto', display: 'flex', paddingBottom: '40px',
                flexDirection: { xs: 'column', md: 'row' }, flexWrap: { xs: 'nowrap', md: 'wrap' }, 
                gap: { xs: '10px', md: '20px' }, justifyContent: 'stretch',
            }}>
                {profileResults.map((p) => {
                    return <Box sx={{ 
                        flex: { xs: 'none', md: '1 0 30%' }, 
                        cursor: 'pointer', display: 'flex', background: 'lightblue',
                        flexDirection: 'row', gap: '14px', height: '10vh', alignItems: 'center',
                        padding: '8px', borderRadius: '8px',
                    }} onClick={() => { nav(`/profile/${p.username}`) }}>
                        { p.pfp ? 
                            <img src={p.pfp ?? ''} alt="pfp" style={{ maxHeight: '90%', aspectRatio: '1',
                                borderRadius: 100, objectFit: 'cover',
                            }}/>
                        :   <Person style={{ fontSize: '7vh', aspectRatio: '1', padding: '1vh', borderRadius: 100, background: 'white' }}/>
                        }
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                            <div style={{ flex: '1', display: 'flex', alignItems: 'center' }}>
                                <Typography><b>{p.display_name}</b></Typography>
                                {(p.type > UserType.User) && 
                                    <Verified sx={{ fontSize: '16px', paddingLeft: '6px', 
                                        color: p.type === UserType.Artist ? 'blue'
                                        : ( p.type === UserType.Admin ? 'yellow' : 'green' ) 
                                    }}/>
                                }
                            </div>
                            <Typography fontSize='14px'>@{p.username}</Typography>
                        </div>
                    </Box>;
                })}
                {/* Fill remaining space (no long profiles) + section off events and profiles */}
                { profileResults.length > 0 && <>
                    <Box sx={{ display: {xs: 'none', md: 'flex' }, flex: '100' }}></Box> 
                    <div style={{ width: '100%' }}></div>
                </>}
                {Array.from(eventResults.values()).map((e) => {
                    return <Box sx={{ 
                        flex: '1 0 40%', justifyContent: 'center',
                        maxWidth: { xs: '100%', md: '48%' }
                    }}>
                        <EventPreview key={e.id} event={e} seeFullEvent={() => { setID(e.id) }}/>
                    </Box>
                })}
            </Box>

            <div style={{ 
                background: 'darkgray', position: 'absolute', zIndex: '2',
                left: '0', width: '100vw', overflow: 'hidden', height: '100%',
                display: fullEventID !== undefined ? 'flex' : 'none'
            }}>
                <div style={{ flex: '1', padding: '14px', zIndex: '1', overflow: 'auto' }}>
                    <EventItem event={fullEventID ? eventResults.get(fullEventID) : undefined } />
                </div>
                <div style={{ zIndex: '2', position: 'absolute', top: '6px', left: '6px'}}>
                    <Button onClick={() => { setID(undefined) }} variant='contained' sx={{ borderRadius: '20px' }}>
                        <ArrowBack fontSize='large'/></Button>
                </div>
            </div>
        </Box>
    </EventMapContext.Provider>);
};